import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/services/api';
import toast from 'react-hot-toast';
import { useAuth } from '@/context/AuthContext';
import { Navigate } from 'react-router-dom';
import { Pencil, Trash2, UserCheck, UserX, CheckCircle2, Circle } from 'lucide-react';

const Container = styled.div`
  padding: 24px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 16px 0;
`;

const Title = styled.h2`
  margin: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 24px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const CardHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
`;

const CardBody = styled.div`
  padding: 16px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  th, td { padding: 10px 12px; border-bottom: 1px solid #f3f4f6; }
  th { text-align: left; color: #6b7280; font-weight: 600; }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin-bottom: 12px;
  font-size: 14px;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 14px;
`;

const Button = styled.button`
  padding: 10px 12px;
  border-radius: 6px;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const ButtonPrimarySmall = styled(Button)`
  padding: 8px 10px;
`;

const RowBadge = styled.span<{variant?: 'admin' | 'inactive'}>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 12px;
  background: ${p => p.variant === 'admin' ? '#EEF2FF' : p.variant === 'inactive' ? '#FEF2F2' : '#F3F4F6'};
  color: ${p => p.variant === 'admin' ? '#4338CA' : p.variant === 'inactive' ? '#991B1B' : '#374151'};
`;

const CriteriaList = styled.div`
  margin: 6px 0 12px 0;
  font-size: 12px;
  color: #6b7280;
`;

const CriteriaItem = styled.div<{ ok?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 4px 0;
  color: ${p => p.ok ? '#10b981' : '#9ca3af'};
`;

const Usuarios: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: usuarios, isFetching } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => apiService.listarUsuarios(),
  });

  const criarMutation = useMutation({
    mutationFn: (input: { name: string; username: string; email: string; password: string; is_admin?: boolean; is_active?: boolean }) => apiService.criarUsuario(input),
    onSuccess: () => {
      toast.success('Usuário criado com sucesso');
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      setForm({ name: '', username: '', email: '', password: '', is_admin: false, is_active: true });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || 'Erro ao criar usuário';
      toast.error(msg);
    }
  });

  const [form, setForm] = useState({ name: '', username: '', email: '', password: '', is_admin: false, is_active: true });
  const [formKey, setFormKey] = useState(0); // força remontagem para evitar autofill
  const [edit, setEdit] = useState<null | { id: string | number; name: string; username: string; email: string; password?: string; is_admin: boolean; is_active: boolean }>(null);

  const editarMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string | number; payload: { name?: string; username?: string; email?: string; password?: string; is_admin?: boolean; is_active?: boolean } }) => apiService.atualizarUsuario(id, payload),
    onSuccess: () => {
      toast.success('Usuário atualizado com sucesso');
      setEdit(null);
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || 'Erro ao atualizar usuário';
      toast.error(msg);
    }
  });

  const deletarMutation = useMutation({
    mutationFn: (id: string) => apiService.deletarUsuario(id),
    onSuccess: () => {
      toast.success('Usuário excluído com sucesso');
      if (edit && String(edit.id) === String(idRef.current)) setEdit(null);
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || 'Erro ao excluir usuário';
      toast.error(msg);
    }
  });

  const idRef = React.useRef<string>('');

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, nextActive }: { id: string; nextActive: boolean }) => apiService.atualizarUsuario(id, { is_active: nextActive }),
    onSuccess: (_data, variables) => {
      toast.success(variables.nextActive ? 'Usuário ativado' : 'Usuário inativado');
      if (edit && String(edit.id) === variables.id) {
        setEdit({ ...edit, is_active: variables.nextActive });
      }
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || 'Erro ao alterar status';
      toast.error(msg);
    }
  });

  const canAccess = useMemo(() => !!user?.is_admin, [user]);
  if (!canAccess) {
    return <Navigate to="/" replace />;
  }

  return (
    <Container>
      <TitleRow>
        <Title>Cadastro de Usuários</Title>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={() => { setEdit(null); setForm({ name: '', username: '', email: '', password: '', is_admin: false, is_active: true }); setFormKey((k) => k + 1); }}>Novo Usuário</Button>
        </div>
      </TitleRow>
      <Grid>
        <Card>
          <CardHeader>Usuários Cadastrados</CardHeader>
          <CardBody>
            <Table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Usuário</th>
                  <th>E-mail</th>
                  <th>Perfil</th>
                  <th>Status</th>
                  <th>Criado em</th>
                  <th style={{ width: 220 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {(usuarios || []).map((u) => (
                  <tr key={u.id}>
                    <td>{u.name}</td>
                    <td>{u.username}</td>
                    <td>{u.email}</td>
                    <td>{(u.is_admin ? 1 : 0) === 1 ? <RowBadge variant="admin">Admin</RowBadge> : 'Usuário'}</td>
                    <td>{(u.is_active ? 1 : 0) === 1 ? 'Ativo' : <RowBadge variant="inactive">Inativo</RowBadge>}</td>
                    <td>{new Date(u.created_at).toLocaleString()}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <ButtonPrimarySmall onClick={() => setEdit({ id: u.id, name: u.name, username: u.username, email: u.email, is_admin: (u.is_admin ? 1 : 0) === 1, is_active: (u.is_active ? 1 : 0) === 1 })}>
                          <Pencil size={16} />
                          Editar
                        </ButtonPrimarySmall>
                        <ButtonPrimarySmall onClick={() => {
                          const next = !((u.is_active ? 1 : 0) === 1);
                          toggleStatusMutation.mutate({ id: String(u.id), nextActive: next });
                        }}>
                          {((u.is_active ? 1 : 0) === 1) ? <><UserX size={16} /> Inativar</> : <><UserCheck size={16} /> Ativar</>}
                        </ButtonPrimarySmall>
                        <ButtonPrimarySmall onClick={() => {
                          if (confirm('Deseja realmente excluir este usuário?')) {
                            idRef.current = String(u.id);
                            deletarMutation.mutate(String(u.id));
                          }
                        }}>
                          <Trash2 size={16} />
                          Excluir
                        </ButtonPrimarySmall>
                      </div>
                    </td>
                  </tr>
                ))}
                {(!usuarios || usuarios.length === 0) && (
                  <tr>
                    <td colSpan={7} style={{ color: '#6b7280', padding: '16px' }}>{isFetching ? 'Carregando...' : 'Nenhum usuário cadastrado'}</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>{edit ? 'Editar Usuário' : 'Novo Usuário'}</CardHeader>
          <CardBody>
            <label>Nome</label>
            <Input value={(edit?.name ?? form.name)} onChange={(e) => edit ? setEdit({ ...edit, name: e.target.value }) : setForm({ ...form, name: e.target.value })} placeholder="Nome completo" />
            <label>Usuário</label>
            <Input
              key={!edit ? `username-${formKey}` : 'username-edit'}
              value={(edit?.username ?? form.username)}
              onChange={(e) => edit ? setEdit({ ...edit, username: e.target.value }) : setForm({ ...form, username: e.target.value })}
              placeholder="login"
              autoComplete={edit ? 'off' : 'off'}
              name={edit ? 'no-autofill-username-edit' : 'no-autofill-username'}
            />
            <label>E-mail</label>
            <Input
              key={!edit ? `email-${formKey}` : 'email-edit'}
              type="email"
              value={(edit?.email ?? form.email)}
              onChange={(e) => edit ? setEdit({ ...edit, email: e.target.value }) : setForm({ ...form, email: e.target.value })}
              placeholder="email@empresa.com"
              autoComplete={edit ? 'off' : 'off'}
              name={edit ? 'no-autofill-email-edit' : 'no-autofill-email'}
              inputMode="email"
            />
            {!edit && (
              <>
                <label>Senha</label>
                <Input
                  key={`password-${formKey}`}
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Senha inicial"
                  autoComplete="new-password"
                  name="no-autofill-password"
                  aria-describedby="password-criteria-create"
                />
                <CriteriaList id="password-criteria-create" aria-live="polite">
                  {(() => { const p = form.password || ''; const lower = /[a-z]/.test(p); const upper = /[A-Z]/.test(p); const number = /\d/.test(p); const special = /[^A-Za-z0-9]/.test(p); return (
                    <>
                      <CriteriaItem ok={lower}>{lower ? <CheckCircle2 size={14} /> : <Circle size={14} />} Contém letra minúscula</CriteriaItem>
                      <CriteriaItem ok={upper}>{upper ? <CheckCircle2 size={14} /> : <Circle size={14} />} Contém letra maiúscula</CriteriaItem>
                      <CriteriaItem ok={number}>{number ? <CheckCircle2 size={14} /> : <Circle size={14} />} Contém número</CriteriaItem>
                      <CriteriaItem ok={special}>{special ? <CheckCircle2 size={14} /> : <Circle size={14} />} Contém caractere especial</CriteriaItem>
                    </>
                  ); })()}
                </CriteriaList>
              </>
            )}
            {edit && (
              <>
                <label>Nova Senha (opcional)</label>
                <Input
                  type="password"
                  value={edit.password || ''}
                  onChange={(e) => setEdit({ ...edit, password: e.target.value })}
                  placeholder="Deixe em branco para manter"
                  autoComplete="new-password"
                  name="no-autofill-new-password"
                  aria-describedby="password-criteria-edit"
                />
                <CriteriaList id="password-criteria-edit" aria-live="polite">
                  {(() => { const p = edit.password || ''; const lower = /[a-z]/.test(p); const upper = /[A-Z]/.test(p); const number = /\d/.test(p); const special = /[^A-Za-z0-9]/.test(p); return (
                    <>
                      <CriteriaItem ok={lower}>{lower ? <CheckCircle2 size={14} /> : <Circle size={14} />} Contém letra minúscula</CriteriaItem>
                      <CriteriaItem ok={upper}>{upper ? <CheckCircle2 size={14} /> : <Circle size={14} />} Contém letra maiúscula</CriteriaItem>
                      <CriteriaItem ok={number}>{number ? <CheckCircle2 size={14} /> : <Circle size={14} />} Contém número</CriteriaItem>
                      <CriteriaItem ok={special}>{special ? <CheckCircle2 size={14} /> : <Circle size={14} />} Contém caractere especial</CriteriaItem>
                    </>
                  ); })()}
                </CriteriaList>
              </>
            )}
            <CheckboxRow>
              <input type="checkbox" checked={(edit ? !!edit.is_admin : form.is_admin)} onChange={(e) => edit ? setEdit({ ...edit, is_admin: e.target.checked }) : setForm({ ...form, is_admin: e.target.checked })} />
              Administrador
            </CheckboxRow>
            <CheckboxRow>
              <input type="checkbox" checked={(edit ? !!edit.is_active : form.is_active)} onChange={(e) => edit ? setEdit({ ...edit, is_active: e.target.checked }) : setForm({ ...form, is_active: e.target.checked })} />
              Ativo
            </CheckboxRow>
            {!edit ? (
              <Button onClick={() => {
                const pwd = form.password || '';
                const strong = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/.test(pwd);
                if (!strong) {
                  toast.error('Senha fraca: inclua minúscula, maiúscula, número e caractere especial');
                  return;
                }
                criarMutation.mutate(form);
              }} disabled={criarMutation.isPending}>
                {criarMutation.isPending ? 'Salvando...' : 'Salvar Usuário'}
              </Button>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <Button onClick={() => {
                  if (edit.password) {
                    const strong = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/.test(edit.password);
                    if (!strong) {
                      toast.error('Senha fraca: inclua minúscula, maiúscula, número e caractere especial');
                      return;
                    }
                  }
                  editarMutation.mutate({ id: edit.id, payload: { name: edit.name, username: edit.username, email: edit.email, password: edit.password, is_admin: edit.is_admin, is_active: edit.is_active } });
                }} disabled={editarMutation.isPending}>
                  {editarMutation.isPending ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
                <Button onClick={() => setEdit(null)}>
                  Cancelar
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      </Grid>
    </Container>
  );
};

export default Usuarios;


